import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, FormGroup, FormControl, ControlLabel, Radio } from 'react-bootstrap';
// import DatePicker from 'react-datepicker';
// import "react-datepicker/dist/react-datepicker.css";
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';   
import { addDaysToDate, formatPhoneNumber, numberToWords, getUser } from '../utils/Common';
import { 
  createPaymentUpdatePortal, 
  getCustomerPaymentMethod, 
  getStripeCustomerSubscriptions, 
  getStripeCustomerPricing, 
  postNewOrder, 
  preconfirmOrder,
  getUsersByBuilder,
  sendOrderConfirmationEmail 
} from '../utils/bedrockAPI';
import AddressDisplay from './AddressDisplay';
import styled from 'styled-components';
import './AddCameraModal.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import LoaderButton from './LoaderButton';

function canManageBilling(user) {
  return !!(user && (
    user.role === 'ADMIN' ||
    user.builderStatus === 'ADMIN' ||
    user.builderStatus === 'BILLING'
  ));
}

const DesktopDateTimePicker = styled(DatePicker)`
    & .MuiInputBase-input {
        font-size: 18px;
        border: 1px solid black;
        padding: 5px;
        cursor: pointer;
        text-align: center;
        max-width: 240px;
        min-height: 40px;
        border-radius: 6px;
    }
    
    & .MuiInputBase-input:hover {
        background-color: #F5F5F5;
    }
  `;

const MobileDateTimePicker = styled(DatePicker)`
    & .MuiInputBase-input {
        font-size: 12px;
        border: 1px solid black;
        padding: 5px;
        cursor: pointer;
        text-align: center;
        max-width: 130px;
        min-height: 25px;
        border-radius: 3px;
    }
    
    & .MuiInputBase-input:hover {
        background-color: #F5F5F5;
    }
`;

export default function AddCameraModal({ show, onHide, stripeCustomer, builderId }) {
  const [step, setStep] = useState(1);
  const [deliveryDate, setDeliveryDate] = useState(addDaysToDate(new Date(), 5));
  const [billingOption, setBillingOption] = useState('existing');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('default');
  const [shippingAddress, setShippingAddress] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const isMountedRef = useRef(true);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editedAddress, setEditedAddress] = useState(null);
  const [stripeSubscriptions, setStripeSubscriptions] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [billingInterval, setBillingInterval] = useState('month');
  const [selectedProducts, setSelectedProducts] = useState([]); // Array of {priceId, quantity, productName}
  const [lastSelectedExistingSubscription, setLastSelectedExistingSubscription] = useState(null);
  const [poNumber, setPoNumber] = useState('');
  const [builderUsers, setBuilderUsers] = useState([]);
  const [selectedPurchaserId, setSelectedPurchaserId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preconfirmData, setPreconfirmData] = useState(null);
  const [isPreconfirming, setIsPreconfirming] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (stripeCustomer?.shipping) {
      const shippingName = stripeCustomer.shipping.name;
      const companyName = stripeCustomer.name || shippingName;
      const phone = formatPhoneNumber(stripeCustomer.phone);
      
      setShippingAddress({
        company: companyName !== shippingName ? companyName : null,
        name: shippingName,
        phone,
        street: stripeCustomer.shipping.address.line1,
        city: stripeCustomer.shipping.address.city,
        state: stripeCustomer.shipping.address.state,
        zip: stripeCustomer.shipping.address.postal_code
      });
    }
    if (stripeCustomer && canManageBilling(getUser())) {
      loadPaymentMethod();
    }
  }, [stripeCustomer]);

  useEffect(() => {
    if(step === 3) {
      loadPaymentMethod();
    }
  }, [step]);

  useEffect(() => {
    if (stripeSubscriptions && stripeSubscriptions.length > 0) {
      // console.log('Setting selected subscription to:', stripeSubscriptions[0].id);
      setSelectedSubscription(stripeSubscriptions[0].id);
      setBillingInterval(stripeSubscriptions[0].items.data[0].price.recurring.interval);
    }
  }, [stripeSubscriptions]);

  useEffect(() => {
    if (shippingAddress) {
      setEditedAddress({
        companyName: shippingAddress.company || '',
        shippingName: shippingAddress.name || '',
        shippingAddress: {
          line1: shippingAddress.street || '',
          line2: '',
          city: shippingAddress.city || '',
          state: shippingAddress.state || '',
          postal_code: shippingAddress.zip || '',
          country: 'US'
        },
        shippingPhone: shippingAddress.phone || ''
      });
    }
  }, [shippingAddress]);

  useEffect(() => {
    let cancelled = false;

    async function loadStripeSubscriptions() {
      try {
        const subscriptions = await getStripeCustomerSubscriptions(builderId);
        if (cancelled || !isMountedRef.current) return;
        setStripeSubscriptions(
          Array.isArray(subscriptions?.subscriptions) ? subscriptions.subscriptions : []
        );
      } catch (error) {
        console.error('Error loading Stripe subscriptions:', error);
        if (!cancelled && isMountedRef.current) {
          setStripeSubscriptions([]);
        }
      }
    }

    async function loadPricingData() {
      try {
        const pricing = await getStripeCustomerPricing(builderId);
        if (!cancelled && isMountedRef.current) {
          setPricingData(pricing);
        }
      } catch (error) {
        console.error('Error loading pricing data:', error);
      }
    }

    if (show && builderId && canManageBilling(getUser())) {
      setStripeSubscriptions(null);
      loadStripeSubscriptions();
      loadPricingData();
      loadBuilderUsers();
    } else if (show) {
      // Avoid leaving the UI stuck on "Loading subscriptions..."
      setStripeSubscriptions([]);
    }

    return () => {
      cancelled = true;
    };
  }, [show, builderId]);

  const loadPaymentMethod = async () => {
    if(builderId) {
      const paymentMethod = await getCustomerPaymentMethod(builderId);
      // console.log(paymentMethod);
      if (isMountedRef.current) {
        setPaymentMethod(paymentMethod);
      }
    }
  }

  const loadBuilderUsers = async () => {
    try {
      const currentUser = getUser();
      if (currentUser && currentUser.role === 'ADMIN' && builderId) {
        const usersData = await getUsersByBuilder(builderId);
        console.log('usersData:', usersData);
        if (isMountedRef.current && usersData) {
          // Filter users to only include those with builderStatus === 'ADMIN' or 'BILLING'
          const filteredUsers = usersData.filter(user => 
            user.builderStatus === 'ADMIN' || user.builderStatus === 'BILLING'
          );
          setBuilderUsers(filteredUsers);
          if(filteredUsers.length === 0) {
            setBuilderUsers([getUser()]);
            setSelectedPurchaserId(currentUser.userId);
          } else {
            // Set the current user as default selected purchaser if they're in the filtered list
            const currentUserInList = filteredUsers.find(user => user.userId === currentUser.userId);
            if (currentUserInList) {
              setSelectedPurchaserId(currentUser.userId);
            } else if (filteredUsers.length > 0) {
              setSelectedPurchaserId(filteredUsers[0].userId);
            }
          }
        } else if (usersData.length === 0) {
          setBuilderUsers([getUser()]);
          setSelectedPurchaserId(currentUser.userId);
        }
      }
    } catch (error) {
      console.error('Error loading builder users:', error);
    }
  };

  // Helper function to format price from pennies to dollars
  const formatPrice = (amountInPennies) => {
    if (!amountInPennies) return '$0.00';
    return `$${(amountInPennies / 100).toFixed(0)}`;
  };

  // Helper function to format billing interval
  const formatBillingInterval = (interval) => {
    if (!interval) return '';
    return interval.charAt(0).toUpperCase() + interval.slice(1);
  };

  // Helper function to format billing date
  const formatBillingDate = (subscription) => {
    if (!subscription.current_period_end) return '';
    const date = new Date(subscription.current_period_end * 1000);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Helper function to filter pricing by interval
  const getFilteredPricing = () => {
    if (!pricingData?.pricing) return [];
    return pricingData.pricing.filter(price => price.recurring.interval === billingInterval);
  };

  const lookupProductName = (priceId) => {
    if (!pricingData?.pricing) return '';
    const price = pricingData.pricing.find(p => p.priceId === priceId);
    return price?.product.name.split(' - ')[0] || '';
  }

  const lookupProductDescription = (priceId) => {
    if (!pricingData?.pricing) return '';
    const price = pricingData.pricing.find(p => p.priceId === priceId);
    return price?.product.description || '';
  }

  const lookupProductPrice = (priceId) => {
    if (!pricingData?.pricing) return '';
    const price = pricingData.pricing.find(p => p.priceId === priceId);
    return price?.unitAmount || 0;
  }

  // Helper function to check for overdue subscriptions
  const hasOverdueSubscriptions = () => {
    if (!stripeSubscriptions) return false;
    return stripeSubscriptions.some(subscription => 
      subscription.status === 'past_due' || subscription.status === 'unpaid'
    );
  };

  // Helper function to get payment method display text
  const getPaymentMethodText = () => {
    if (!paymentMethod) return 'Use default payment method on file';
    
    switch (paymentMethod.type) {
      case 'card':
        return 'Use default card on file';
      case 'us_bank_account':
        return 'Use default bank account on file';
      default:
        return 'Use default payment method on file';
    }
  };

  // Helper function to calculate subscription total
  const calculateSubscriptionTotal = (subscription) => {
    if (!subscription?.items?.data) return 0;
    return subscription.items.data.reduce((total, item) => {
      if (item.price && item.price.id) {
        return total + (item.price.unit_amount * (item.quantity || 1));
      } else if (item.priceId) {
        return total + (lookupProductPrice(item.priceId) * (item.quantity || 1));
      }
      return total;
    }, 0);
  };

  // Helper function to get subscription items breakdown
  const getSubscriptionItemsBreakdown = (subscription) => {
    if (!subscription?.items?.data) return [];
    return subscription.items.data
      .filter(item => item?.priceId || item?.price?.id) // Filter out items without price data
      .map(item => ({
        id: item.id,
        priceId: item.priceId || item.price.id,
        productName: lookupProductName(item.priceId || item.price.id),
        productDescription: lookupProductDescription(item.priceId || item.price.id),
        unitAmount: lookupProductPrice(item.priceId || item.price.id),
        quantity: item.quantity || 1,
        total: lookupProductPrice(item.priceId || item.price.id) * (item.quantity || 1),
        interval: item.interval || 'month'
      }));
  };

  // Helper function to get updated subscription with new product
  const getUpdatedSubscriptionWithProduct = (subscription, newProductId) => {
    if (!subscription || !newProductId) return subscription;
    
    const items = getSubscriptionItemsBreakdown(subscription);
    const newProduct = pricingData?.pricing?.find(p => p.priceId === newProductId);
    
    if (!newProduct) return subscription;
    
    // Check if product already exists in subscription
    const existingItem = items.find(item => item.priceId === newProductId);
    
    if (existingItem) {
      // Update quantity of existing item
      const updatedItems = items.map(item => 
        item.priceId === newProductId 
          ? { ...item, quantity: item.quantity + 1, total: item.unitAmount * (item.quantity + 1) }
          : item
      );
      return {
        ...subscription,
        items: { data: updatedItems },
        total: updatedItems.reduce((sum, item) => sum + item.total, 0)
      };
    } else {
      // Add new item
      const newItem = {
        id: `new-${Date.now()}`,
        priceId: newProductId,
        productName: lookupProductName(newProductId),
        productDescription: lookupProductDescription(newProductId),
        unitAmount: newProduct.unitAmount,
        quantity: 1,
        total: newProduct.unitAmount,
        interval: newProduct.recurring.interval
      };
      const updatedItems = [...items, newItem];
      return {
        ...subscription,
        items: { data: updatedItems },
        total: updatedItems.reduce((sum, item) => sum + item.total, 0)
      };
    }
  };

  const getSubscriptionName = (subscription) => {
    if (!subscription?.items?.data) return 'Subscription';
    const validItems = subscription.items.data.filter(item => item?.price?.id);
    return `${numberToWords(validItems.length, true)} Camera System${validItems.length > 1 ? 's' : ''}`;
  };

  // Helper functions for managing selected products
  const addProductToSelection = (priceId, quantity = 1) => {
    const product = pricingData?.pricing?.find(p => p.priceId === priceId);
    if (!product) return;

    setSelectedProducts(prev => {
      const existingIndex = prev.findIndex(p => p.priceId === priceId);
      if (existingIndex >= 0) {
        // Update quantity of existing product
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity
        };
        return updated;
      } else {
        // Add new product
        return [...prev, {
          priceId,
          quantity,
          productName: product.product.name,
          unitAmount: product.unitAmount,
          interval: product.recurring.interval
        }];
      }
    });
  };

  const updateProductQuantity = (priceId, quantity) => {
    if (quantity <= 0) {
      removeProductFromSelection(priceId);
      return;
    }

    setSelectedProducts(prev => {
      const existingIndex = prev.findIndex(p => p.priceId === priceId);
      
      if (existingIndex >= 0) {
        // Update existing product quantity
        return prev.map(p => p.priceId === priceId ? { ...p, quantity } : p);
      } else {
        // Add new product
        const product = pricingData?.pricing?.find(p => p.priceId === priceId);
        if (!product) return prev;
        
        return [...prev, {
          priceId,
          quantity,
          productName: product.product.name,
          unitAmount: product.unitAmount,
          interval: product.recurring.interval
        }];
      }
    });
  };

  const removeProductFromSelection = (priceId) => {
    setSelectedProducts(prev => prev.filter(p => p.priceId !== priceId));
  };

  const getSelectedProductsTotal = () => {
    return selectedProducts.reduce((total, product) => {
      return total + (product.unitAmount * product.quantity);
    }, 0);
  };

  const getUpdatedSubscriptionWithProducts = (subscription) => {
    if (!subscription || selectedProducts.length === 0) return subscription;
    
    const items = getSubscriptionItemsBreakdown(subscription);
    const updatedItems = [...items];
    
    selectedProducts.forEach(selectedProduct => {
      const existingItemIndex = updatedItems.findIndex(item => item.priceId === selectedProduct.priceId);
      
      if (existingItemIndex >= 0) {
        // Update quantity of existing item
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + selectedProduct.quantity,
          total: updatedItems[existingItemIndex].unitAmount * (updatedItems[existingItemIndex].quantity + selectedProduct.quantity)
        };
      } else {
        // Add new item
        updatedItems.push({
          id: `new-${Date.now()}-${selectedProduct.priceId}`,
          priceId: selectedProduct.priceId,
          productName: selectedProduct.productName,
          productDescription: lookupProductDescription(selectedProduct.priceId),
          unitAmount: selectedProduct.unitAmount,
          quantity: selectedProduct.quantity,
          total: selectedProduct.unitAmount * selectedProduct.quantity,
          interval: selectedProduct.interval
        });
      }
    });
    
    return {
      ...subscription,
      items: { data: updatedItems },
      total: updatedItems.reduce((sum, item) => sum + item.total, 0)
    };
  };

  const handleClose = () => {
    setStep(1);
    // setDeliveryDate(new Date());
    setBillingOption('existing');
    setSelectedPaymentMethod('default');
    setSelectedSubscription(null);
    setSelectedProducts([]);
    setLastSelectedExistingSubscription(null);
    setBillingInterval('month');
    setPoNumber('');
    setSelectedPurchaserId('');
    setBuilderUsers([]);
    setIsSubmitting(false);
    onHide();
  };

  const handlePreconfirm = async () => {
    setIsPreconfirming(true);
    try {
      // Determine purchaser ID - use selected purchaser for admin users, otherwise use current user
      const currentUser = getUser();
      const purchaserId = (currentUser.role === 'ADMIN' && selectedPurchaserId) 
        ? selectedPurchaserId 
        : currentUser.userId;

      // Get reference subscription and payment method ID
      const referenceSubscription = billingOption === 'new' && stripeSubscriptions && stripeSubscriptions.length > 0
        ? stripeSubscriptions[0].id
        : undefined;
      const paymentMethodId = paymentMethod?.id;

      const subscriptionId = billingOption === 'existing' ? selectedSubscription : 'NEW';

      // Create the order data for preconfirmation
      const orderData = {
        deliveryDate: deliveryDate.toISOString(),
        subscriptionId: subscriptionId,
        builderId: builderId,
        purchaserId: purchaserId,
        status: 'REORDER',
        orderMeta: {
          pricing: {
            items: selectedProducts.map(product => ({
              price: product.priceId,
              quantity: product.quantity
            }))
          },
          ...(referenceSubscription && { referenceSubscription }),
          ...(paymentMethodId && { paymentMethod: paymentMethodId })
        },
        ...(poNumber && { poNumber })
      };

      console.log('Preconfirming order:', {orders: [orderData]});
      const preconfirmResult = await preconfirmOrder({orders: [orderData]});
      console.log('Preconfirm result:', preconfirmResult);
      setPreconfirmData(preconfirmResult ? preconfirmResult.results[0] : null);
      setStep(5);
    } catch (error) {
      console.error('Error preconfirming order:', error);
      // Still proceed to step 5 but without preconfirm data
      setStep(5);
    } finally {
      setIsPreconfirming(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const currentUser = getUser();
      if (!currentUser || !currentUser.userId) {
        console.error('No current user found');
        setIsSubmitting(false);
        return;
      }

      // Validate required data
      if (!deliveryDate || !builderId || selectedProducts.length === 0) {
        console.error('Missing required order data');
        setIsSubmitting(false);
        return;
      }

      // Validate purchaser selection for admin users
      if (currentUser.role === 'ADMIN' && !selectedPurchaserId) {
        console.error('Admin user must select a purchaser');
        setIsSubmitting(false);
        return;
      }

      // Determine subscription ID
      const subscriptionId = billingOption === 'existing' ? selectedSubscription : 'NEW';

      // Get reference subscription for new subscriptions
      const referenceSubscription = billingOption === 'new' && stripeSubscriptions && stripeSubscriptions.length > 0 
        ? stripeSubscriptions[0].id 
        : undefined;

      // Get payment method ID
      const paymentMethodId = paymentMethod?.id;

      // Determine purchaser ID - use selected purchaser for admin users, otherwise use current user
      const purchaserId = (currentUser.role === 'ADMIN' && selectedPurchaserId) 
        ? selectedPurchaserId 
        : currentUser.userId;

      // Create a single order with all selected products and their quantities
      const orderData = {
        deliveryDate: deliveryDate.toISOString(),
        subscriptionId: subscriptionId,
        builderId: builderId,
        purchaserId: purchaserId,
        status: 'REORDER',
        orderMeta: {
          pricing: {
            items: selectedProducts.map(product => ({
              price: product.priceId,
              quantity: product.quantity
            }))
          },
          ...(referenceSubscription && { referenceSubscription }),
          ...(paymentMethodId && { paymentMethod: paymentMethodId })
        },
        ...(poNumber && { poNumber })
      };

      console.log('Creating order with multiple items:', orderData);
      const result = await postNewOrder(orderData);

      // Handle the single order result
      const orderIds = {'orderIds': result.map(r => r.orderId)};
      console.log('Order IDs:', orderIds);
      // TODO TODO TODO - TEST THIS!
      await sendOrderConfirmationEmail(orderIds);
      console.log('Orders created successfully:', result);

      setStep(6);
    } catch (error) {
      console.error('Error creating orders:', error);
      // You might want to show an error message to the user here
      // For now, we'll still proceed to step 5
      setStep(5);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDeliveryDateStep1 = () => (
    <div>
      <h4 className="modal-step-title">When do you need the camera system?</h4>
      <p className="modal-step-description">
        We will do our best to ship your camera system to arrive by this date. If there are any delays, we'll notify you immediately.
      </p>
      <div className="modal-step-content">
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          {isMobile ? (
            <MobileDateTimePicker
              value={deliveryDate}
              onChange={date => setDeliveryDate(date)}
              minDate={addDaysToDate(new Date(), 4)}
              InputProps={{disableUnderline:true}}
            />
          ) : (
            <DesktopDateTimePicker
              value={deliveryDate}
              onChange={date => setDeliveryDate(date)}
              minDate={addDaysToDate(new Date(), 4)}
              InputProps={{disableUnderline:true}}
            />
          )}
        </MuiPickersUtilsProvider>
      </div>
      <div className="modal-footer">
        <Button className="btn-brand-dark btn-compact" onClick={() => setStep(2)}>Next</Button>
      </div>
    </div>
  );

  // TODO TODO TODO - This needs invoice info to make this clear to the customer
  const renderBillingOptionStep2 = () => (
    <div>
      <h4 className="modal-step-title">How would you like to handle billing for this camera?</h4>
      
      {/* Overdue Subscription Warning */}
      {hasOverdueSubscriptions() && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px',
          color: '#856404'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            gap: '10px'
          }}>
            <div style={{ 
              fontSize: '18px', 
              color: '#f39c12',
              marginTop: '2px'
            }}>
              ⚠️
            </div>
            <div>
              <div style={{ 
                fontWeight: '600', 
                fontSize: '16px', 
                marginBottom: '8px',
                color: '#856404'
              }}>
                Payment Issue Detected
              </div>
              <div style={{ 
                fontSize: '14px', 
                marginBottom: '10px',
                lineHeight: '1.4'
              }}>
                You have overdue subscription payments that need to be resolved before adding new camera systems. 
                Please contact our support team to resolve this payment issue.
              </div>
              <div style={{ 
                fontSize: '14px', 
                fontWeight: '600',
                color: '#856404'
              }}>
                📞 Call us at: <a 
                  href="tel:+17047022152" 
                  style={{ 
                    color: '#007bff', 
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                >
                  (704) 702-2152
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      <Form>
        <Radio
          name="billingOption"
          value="existing"
          checked={billingOption === 'existing'}
          onChange={() => {
            setBillingOption('existing');
            // Clear selected products when switching billing options
            setSelectedProducts([]);
            // Restore the last selected existing subscription, or default to first
            if (lastSelectedExistingSubscription && stripeSubscriptions?.some(s => s.id === lastSelectedExistingSubscription)) {
              setSelectedSubscription(lastSelectedExistingSubscription);
              // Set billing interval based on the selected subscription
              const subscription = stripeSubscriptions.find(s => s.id === lastSelectedExistingSubscription);
              if (subscription?.items?.data?.[0]?.price?.recurring?.interval) {
                setBillingInterval(subscription.items.data[0].price.recurring.interval);
              }
            } else if (stripeSubscriptions && stripeSubscriptions.length > 0) {
              setSelectedSubscription(stripeSubscriptions[0].id);
              setLastSelectedExistingSubscription(stripeSubscriptions[0].id);
              // Set billing interval based on the first subscription
              if (stripeSubscriptions[0]?.items?.data?.[0]?.price?.recurring?.interval) {
                setBillingInterval(stripeSubscriptions[0].items.data[0].price.recurring.interval);
              }
            } else {
              // No existing subscriptions: ensure none is selected
              setSelectedSubscription(null);
            }
          }}
          className="modal-radio"
        >
          Include with existing subscription
        </Radio>
        <Radio
          name="billingOption"
          value="new"
          checked={billingOption === 'new'}
          onChange={() => {
            setBillingOption('new');
            setSelectedSubscription('new');
            // Clear selected products when switching billing options
            setSelectedProducts([]);
            // Reset billing interval to default (monthly) for new subscriptions
            setBillingInterval('month');
          }}
          className="modal-radio"
        >
          Create new subscription with separate invoices
        </Radio>
      </Form>
      
      {/* Subscription Selection */}
      <div className="subscription-selection" style={{ marginTop: '20px' }}>

        
        {billingOption === 'existing' && (
          <div>
            <h5 style={{ 
              marginBottom: '15px',
              fontFamily: 'Open Sans, sans-serif',
              fontSize: '16px',
              fontWeight: '600',
              color: '#212529'
            }}>Select an existing subscription to add the new camera system to:</h5>
            
            {/* Subscription Selection */}
            {stripeSubscriptions && stripeSubscriptions.length > 0 ? (
              stripeSubscriptions.map((subscription, index) => {
                const isSelected = selectedSubscription === subscription.id;
                const itemsBreakdown = getSubscriptionItemsBreakdown(subscription);
                const currentTotal = calculateSubscriptionTotal(subscription);
                const subscriptionInterval = subscription.items.data[0]?.price?.recurring?.interval || 'month';
                
                return (
                  <div 
                    key={subscription.id}
                    className="subscription-option"
                    style={{
                      border: isSelected ? '2px solid #007bff' : '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '15px',
                      marginBottom: '15px',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#e3f2fd' : '#fff',
                    }}
                    onClick={() => {
                      setSelectedSubscription(subscription.id);
                      setLastSelectedExistingSubscription(subscription.id);
                      setBillingInterval(subscriptionInterval);
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Radio
                        name="subscription"
                        value={subscription.id}
                        checked={isSelected}
                        onChange={() => setSelectedSubscription(subscription.id)}
                        style={{ marginRight: '15px', marginTop: '2px' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>
                          {getSubscriptionName(subscription)}
                        </div>
                        {subscription.description && (
                          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                            {subscription.description}
                          </div>
                        )}
                        
                        {/* Current Subscription Breakdown */}
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '5px', color: '#495057' }}>
                            Current Items:
                          </div>
                          {itemsBreakdown.map((item, itemIndex) => (
                            <div key={item.id} style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '4px 0 0 20px',
                              fontSize: '13px'
                            }}>
                              <div style={{ flex: 1 }}>
                                <span style={{ fontWeight: '500' }}>{item.productName}</span>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <span style={{ color: '#666' }}>Qty: {item.quantity}</span>
                                <span style={{ marginLeft: '10px', fontWeight: '600' }}>
                                  {formatPrice(item.total)}/{item.interval}
                                </span>
                              </div>
                            </div>
                          ))}
                          <div style={{ 
                            borderTop: '1px solid #ddd', 
                            paddingTop: '5px', 
                            marginTop: '5px',
                            display: 'flex', 
                            justifyContent: 'space-between',
                            fontWeight: '600',
                            fontSize: '14px'
                          }}>
                            <span>Current Total:</span>
                            <span style={{ color: '#2E7D32' }}>{formatPrice(currentTotal)}/{subscriptionInterval}</span>
                          </div>
                        </div>
                        
                        <div style={{ fontSize: '12px', color: '#888' }}>
                          Next billing: {formatBillingDate(subscription)} | Status: {subscription.status}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px', 
                border: '1px solid #dee2e6',
                color: '#6c757d',
                fontSize: '14px'
              }}>
                {stripeSubscriptions === null ? 'Loading subscriptions...' : 'No existing subscriptions found. Please select "Create new subscription" to proceed.'}
              </div>
            )}

            {/* Product Selection Section - Only show when a real existing subscription is selected */}
            {selectedSubscription && selectedSubscription !== 'new' && (
              <div style={{ 
                marginTop: '20px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <h6 style={{ 
                  marginBottom: '15px',
                  fontFamily: 'Open Sans, sans-serif',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#495057'
                }}>
                  Select products to add to your subscription:
                </h6>
                
                {pricingData ? (
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {getFilteredPricing().map((price) => {
                      const selectedProduct = selectedProducts.find(p => p.priceId === price.priceId);
                      const quantity = selectedProduct ? selectedProduct.quantity : 0;
                      
                      return (
                        <div 
                          key={price.priceId}
                          style={{
                            border: quantity > 0 ? '2px solid #007bff' : '1px solid #ddd',
                            borderRadius: '6px',
                            padding: '12px',
                            backgroundColor: quantity > 0 ? '#e3f2fd' : '#fff',
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                              <div style={{ marginRight: '12px' }}>
                                <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '2px' }}>
                                  {price.product.name}
                                </div>
                                {price.product.description && (
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {price.product.description}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ textAlign: 'right', marginRight: '10px' }}>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: '#2E7D32' }}>
                                  {formatPrice(price.unitAmount)}/{billingInterval}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <Button
                                  bsSize="sm"
                                  onClick={() => updateProductQuantity(price.priceId, quantity - 1)}
                                  disabled={quantity <= 0}
                                  style={{ minWidth: '30px' }}
                                >
                                  -
                                </Button>
                                <span style={{ 
                                  minWidth: '30px', 
                                  textAlign: 'center', 
                                  fontWeight: '600',
                                  fontSize: '14px'
                                }}>
                                  {quantity}
                                </span>
                                <Button
                                  bsSize="sm"
                                  onClick={() => updateProductQuantity(price.priceId, quantity + 1)}
                                  style={{ minWidth: '30px' }}
                                >
                                  +
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                    Loading product options...
                  </div>
                )}

                {/* Selected Products Summary */}
                {selectedProducts.length > 0 && (
                  <div style={{ 
                    marginTop: '15px',
                    padding: '15px',
                    backgroundColor: '#f8fff9',
                    borderRadius: '6px',
                    border: '1px solid #28a745'
                  }}>
                    <div style={{ 
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#28a745',
                      marginBottom: '10px'
                    }}>
                      Selected Products:
                    </div>
                    {selectedProducts.map((product) => (
                      <div key={product.priceId} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '5px 0',
                        fontSize: '13px'
                      }}>
                        <span>{product.productName} (Qty: {product.quantity})</span>
                        <span style={{ fontWeight: '600' }}>
                          {formatPrice(product.unitAmount * product.quantity)}/{product.interval}
                        </span>
                      </div>
                    ))}
                    <div style={{ 
                      borderTop: '1px solid #28a745',
                      paddingTop: '8px',
                      marginTop: '8px',
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#28a745'
                    }}>
                      <span>Additional Monthly Total:</span>
                      <span>{formatPrice(getSelectedProductsTotal())}/{billingInterval}</span>
                    </div>
                  </div>
                )}

                {/* Updated Total Display */}
                {selectedProducts.length > 0 && (
                  <div style={{ 
                    marginTop: '10px',
                    padding: '12px',
                    backgroundColor: '#e8f5e8',
                    borderRadius: '6px',
                    border: '1px solid #28a745'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#28a745'
                    }}>
                      <span>Updated Monthly Total:</span>
                      <span>{formatPrice(calculateSubscriptionTotal(getUpdatedSubscriptionWithProducts(
                        stripeSubscriptions.find(s => s.id === selectedSubscription)
                      )))}/{billingInterval}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {billingOption === 'new' && (
          <div>
            <h5 style={{ 
              marginBottom: '15px',
              fontFamily: 'Open Sans, sans-serif',
              fontSize: '16px',
              fontWeight: '600',
              color: '#212529'
            }}>Select Product and Billing:</h5>
            
            {/* Billing Interval Toggle */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ 
                display: 'flex', 
                border: '1px solid #ddd', 
                borderRadius: '8px', 
                overflow: 'hidden',
                width: 'fit-content'
              }}>
                <button
                  type="button"
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    background: billingInterval === 'month' ? '#007bff' : '#f8f9fa',
                    color: billingInterval === 'month' ? 'white' : '#495057',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: billingInterval === 'month' ? '600' : '400'
                  }}
                  onClick={() => {
                    setBillingInterval('month');
                    // Clear selected products when switching billing intervals
                    setSelectedProducts([]);
                  }}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    background: billingInterval === 'year' ? '#007bff' : '#f8f9fa',
                    color: billingInterval === 'year' ? 'white' : '#495057',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: billingInterval === 'year' ? '600' : '400'
                  }}
                  onClick={() => {
                    setBillingInterval('year');
                    // Clear selected products when switching billing intervals
                    setSelectedProducts([]);
                  }}
                >
                  Yearly
                </button>
              </div>
            </div>

            {/* Product Selection */}
            {pricingData ? (
              <div style={{ display: 'grid', gap: '10px' }}>
                {getFilteredPricing().map((price) => {
                  const selectedProduct = selectedProducts.find(p => p.priceId === price.priceId);
                  const quantity = selectedProduct ? selectedProduct.quantity : 0;
                  
                  return (
                    <div 
                      key={price.priceId}
                      style={{
                        border: quantity > 0 ? '2px solid #007bff' : '1px solid #ddd',
                        borderRadius: '6px',
                        padding: '12px',
                        backgroundColor: quantity > 0 ? '#e3f2fd' : '#fff',
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                          <div style={{ marginRight: '12px' }}>
                            <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '2px' }}>
                              {price.product.name}
                            </div>
                            {price.product.description && (
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {price.product.description}
                              </div>
                            )}
                            {billingInterval === 'year' && (
                              <div style={{ fontSize: '12px', color: '#007bff', fontWeight: '600', marginTop: '4px' }}>
                                Save {formatPrice(price.unitAmount/0.9/10)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ textAlign: 'right', marginRight: '10px' }}>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: '#2E7D32' }}>
                              {formatPrice(price.unitAmount)}/{billingInterval}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <Button
                              bsSize="xs"
                              onClick={() => updateProductQuantity(price.priceId, quantity - 1)}
                              disabled={quantity <= 0}
                              style={{ minWidth: '30px' }}
                            >
                              -
                            </Button>
                            <span style={{ 
                              minWidth: '30px', 
                              textAlign: 'center', 
                              fontWeight: '600',
                              fontSize: '14px'
                            }}>
                              {quantity}
                            </span>
                            <Button
                              bsSize="xs"
                              onClick={() => updateProductQuantity(price.priceId, quantity + 1)}
                              style={{ minWidth: '30px' }}
                            >
                              +
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px', 
                border: '1px solid #dee2e6',
                color: '#6c757d',
                fontSize: '14px'
              }}>
                Loading product options...
              </div>
            )}

            {/* Selected Products Summary */}
            {selectedProducts.length > 0 && (
              <div style={{ 
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f8fff9',
                borderRadius: '6px',
                border: '1px solid #28a745'
              }}>
                <div style={{ 
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#28a745',
                  marginBottom: '10px'
                }}>
                  Selected Products:
                </div>
                {selectedProducts.map((product) => (
                  <div key={product.priceId} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '5px 0',
                    fontSize: '13px'
                  }}>
                    <span>{product.productName} (Qty: {product.quantity})</span>
                    <span style={{ fontWeight: '600' }}>
                      {formatPrice(product.unitAmount * product.quantity)}/{product.interval}
                    </span>
                  </div>
                ))}
                <div style={{ 
                  borderTop: '1px solid #28a745',
                  paddingTop: '8px',
                  marginTop: '8px',
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#28a745'
                }}>
                  <span>Total Monthly Cost:</span>
                  <span>{formatPrice(getSelectedProductsTotal())}/{billingInterval}</span>
                </div>
              </div>
            )}
          </div>
        )}
        

      </div>
      
      <div className="modal-footer">
        <Button className="btn-brand-outline btn-compact" onClick={() => setStep(1)}>Back</Button>
        <Button
          className="btn-brand-dark btn-compact"
          onClick={() => setStep(3)}
          disabled={hasOverdueSubscriptions() || (billingOption === 'existing' ? !selectedSubscription || selectedProducts.length === 0 : selectedProducts.length === 0)}
        >
          Next
        </Button>
      </div>
    </div>
  );

  const renderPaymentMethodStep3 = () => (
    <div>
      <h4 className="modal-step-title">How would you like to pay for this camera?</h4>
      <Form>
        <Radio
          name="paymentMethod"
          value="default"
          checked={selectedPaymentMethod === 'default'}
          onChange={() => setSelectedPaymentMethod('default')}
          className="modal-radio"
        >
          {getPaymentMethodText()}
        </Radio>
        {selectedPaymentMethod === 'default' && paymentMethod && (
        <div className="payment-method-display">
          <div className="card-icon">
            {paymentMethod.type === 'card' && (
              <>
                {paymentMethod.card.brand === 'visa' && <i className="fab fa-cc-visa"></i>}
                {paymentMethod.card.brand === 'mastercard' && <i className="fab fa-cc-mastercard"></i>}
                {paymentMethod.card.brand === 'amex' && <i className="fab fa-cc-amex"></i>}
                {paymentMethod.card.brand === 'discover' && <i className="fab fa-cc-discover"></i>}
                {!['visa', 'mastercard', 'amex', 'discover'].includes(paymentMethod.card.brand) && 
                  <i className="far fa-credit-card"></i>}
              </>
            )}
            {paymentMethod.type === 'us_bank_account' && <i className="fas fa-university"></i>}
            {!['card', 'us_bank_account'].includes(paymentMethod.type) && <i className="fas fa-money-bill-wave"></i>}
          </div>
          <div className="card-details">
            <div className="card-info">
              {paymentMethod.type === 'card' && (
                <>
                  <span className="card-brand">{paymentMethod.card.display_brand.replace('_', ' ').toUpperCase()}</span>
                  <span className="card-number">•••• {paymentMethod.card.last4}</span>
                </>
              )}
              {paymentMethod.type === 'us_bank_account' && (
                <>
                  <span className="card-brand">{paymentMethod.us_bank_account.bank_name}</span>
                  <span className="card-number">•••• {paymentMethod.us_bank_account.last4}</span>
                </>
              )}
              {!['card', 'us_bank_account'].includes(paymentMethod.type) && (
                <span className="card-brand">{paymentMethod.type.replace('_', ' ').toUpperCase()}</span>
              )}
            </div>
            {paymentMethod.type === 'card' && (
              <div className="card-expiry">Expires {paymentMethod.card.exp_month}/{paymentMethod.card.exp_year}</div>
            )}
            {paymentMethod.type === 'us_bank_account' && (
              <div className="card-expiry">{paymentMethod.us_bank_account.account_type.charAt(0).toUpperCase() + paymentMethod.us_bank_account.account_type.slice(1)} Account</div>
            )}
          </div>
        </div>
      )}
        <Button
          className="btn-brand-outline btn-compact update-payment-btn"
          onClick={async () => {
            try {
              const { url } = await createPaymentUpdatePortal(builderId);
              window.open(url, '_blank');
              setStep(2);
            } catch (error) {
              console.error('Error opening payment portal:', error);
              // You might want to show an error message to the user here
            }
          }}
        >
          Update Payment Method
        </Button>
      </Form>
      
      <div className="modal-footer">
        <Button className="btn-brand-outline btn-compact" onClick={() => setStep(2)}>Back</Button>
        <Button className="btn-brand-dark btn-compact" onClick={() => setStep(4)}>Next</Button>
      </div>
    </div>
  );

  const renderShippingAddressStep4 = () => (
    <div>
      <h4 className="modal-step-title">Please confirm your shipping address</h4>
        <AddressDisplay
          address={shippingAddress}
          setShippingAddress={setShippingAddress}
          builderId={builderId}
          isEditingAddress={isEditingAddress}
          setIsEditingAddress={setIsEditingAddress}
        />
        
        {/* PO Number Field */}
        <div style={{ marginTop: '20px' }}>
          {/* <h5 style={{ 
            marginBottom: '15px',
            fontFamily: 'Open Sans, sans-serif',
            fontSize: '16px',
            fontWeight: '600',
            color: '#212529'
          }}>Purchase Order Information (Optional)</h5> */}
          <FormGroup>
            <h4 className="modal-step-title">Purchase order number (optional)</h4>
            <FormControl
              type="text"
              placeholder="Enter your PO number"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
            />
          </FormGroup>
        </div>

        {/* Purchaser Selection for Admin Users */}
        {getUser().role === 'ADMIN' && builderUsers.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <FormGroup>
              <h4 className="modal-step-title" style={{color: '#cc0000'}}>Override Purchaser (ADMIN ONLY)</h4>
              <FormControl
                componentClass="select"
                value={selectedPurchaserId}
                onChange={(e) => setSelectedPurchaserId(e.target.value)}
              >
                <option value="">Select a purchaser</option>
                {builderUsers.map(user => (
                  <option key={user.userId} value={user.userId}>
                    {user.firstName} {user.lastName} ({user.email}) - {user.builderStatus}
                  </option>
                ))}
              </FormControl>
            </FormGroup>
          </div>
        )}
        
      <div className="modal-footer">
        <Button className="btn-brand-outline btn-compact" onClick={() => setStep(3)} disabled={isSubmitting}>Back</Button>
        <LoaderButton
          className="btn-brand-dark btn-compact"
          onClick={handlePreconfirm}
          isLoading={isPreconfirming}
          disabled={!shippingAddress || isEditingAddress || (getUser().role === 'ADMIN' && !selectedPurchaserId) || isPreconfirming}
        >
          Review Order
        </LoaderButton>
      </div>
    </div>
  );

  const renderOrderConfirmationStep5 = () => (
    <div>
      <h4 className="modal-step-title">Order Review & Billing Summary</h4>
      
      {/* Show billing information if available */}
      {preconfirmData ? (
        <div>
          <div className="billing-summary">
            <h5 className="billing-summary-title">Billing Summary</h5>
            
            {/* Order Items */}
            <div style={{ marginBottom: '15px' }}>
              <h6 className="billing-summary-subtitle">Order Items:</h6>
              {selectedProducts.map((product, index) => (
                <div key={index} className="billing-summary-item">
                  <span>{product.productName} × {product.quantity}</span>
                </div>
              ))}
            </div>

            {/* Prorated Amount (if any) */}
            {preconfirmData.totalProratedAmount > 0 && (
              <div>
                <div className="billing-summary-row">
                  <span className="billing-summary-row-label">Prorated amount to be charged immediately ({preconfirmData.prorationDays} days from the delivery date to the next billing date):</span>
                  <span className="billing-summary-row-amount">
                    ${(preconfirmData.totalProratedAmount / 100).toFixed(2)}
                  </span>
                </div>
                <div className="billing-summary-date-range">
                  {deliveryDate.toLocaleDateString()} → {preconfirmData.subscriptionBillingDate ? new Date(preconfirmData.subscriptionBillingDate).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            )}

            {/* MSRP (if discount) */}
            {preconfirmData.totalDiscount > 0 && (
              <div className="billing-summary-row">
                <span className="billing-summary-row-label">Original Price (MSRP):</span>
                <span className="billing-summary-row-amount" style={{ textDecoration: 'line-through' }}>
                  ${(preconfirmData.totalDefaultAmount / 100).toFixed(2)}/{billingInterval}
                </span>
              </div>
            )}

            {/* Discount Applied */}
            {preconfirmData.totalDiscount > 0 && (
              <div className="billing-summary-row">
                <span className="billing-summary-row-discount">Grandfathered Pricing Discount ({preconfirmData.discountPercent}%):</span>
                <span className="billing-summary-row-amount-discount">
                  -${(preconfirmData.totalDiscount / 100).toFixed(2)}/{billingInterval}
                </span>
              </div>
            )}

            {/* Amount to be Added */}
            <div className="billing-summary-row">
              <span className="billing-summary-row-label">Amount to be added to your normal billing on ({preconfirmData.subscriptionBillingDate ? new Date(preconfirmData.subscriptionBillingDate).toLocaleDateString() : 'N/A'}):</span>
              <span className="billing-summary-row-amount">
                ${(preconfirmData.totalAmount / 100).toFixed(2)}/{billingInterval}
              </span>
            </div>


          </div>

          <div className="modal-footer">
            <Button className="btn-brand-outline btn-compact" onClick={() => setStep(4)}>Back</Button>
            <LoaderButton
              className="btn-brand-dark btn-compact"
              onClick={handleSubmit}
              isLoading={isSubmitting}
            >
              Place Order
            </LoaderButton>
          </div>
        </div>
      ) : (
        <div>
          <p className="modal-step-description">
            {isPreconfirming ? 'Calculating billing information...' : 'Unable to load billing information. Please try again.'}
          </p>
          <div className="modal-footer">
            <Button className="btn-brand-outline btn-compact" onClick={() => setStep(4)}>Back</Button>
            <Button className="btn-brand-outline btn-compact" onClick={handleClose}>Close</Button>
          </div>
        </div>
      )}
    </div>
  );

  const renderThankYouStep6 = () => (
    <div>
      <h4 className="modal-step-title">Thank You for Your Order!</h4>
      <p className="modal-step-description">
        Your order has been successfully placed. You will receive an email confirmation shortly with all the details.
      </p>
      <div className="modal-footer">
        <Button className="btn-brand-dark btn-compact" onClick={handleClose}>Close</Button>
      </div>
    </div>
  );

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      size="lg" 
      className="camera-modal"
      enforceFocus={false}
      restoreFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Order New Camera</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 1 && renderDeliveryDateStep1()}
        {step === 2 && renderBillingOptionStep2()}
        {step === 3 && renderPaymentMethodStep3()}
        {step === 4 && renderShippingAddressStep4()}
        {step === 5 && renderOrderConfirmationStep5()}
        {step === 6 && renderThankYouStep6()}
      </Modal.Body>
    </Modal>
  );
} 